import React, { useContext, useEffect, useRef, useState } from 'react'
import { campaignContact, campaignContactData, campaignContactFieldData, getListOfCampaignContactFieldResponse, globalMenuItemData } from '../../../models/axiosModel';
import { Checkbox, Grid, TableCell, Tooltip, Typography } from '@mui/material';
import GlobalMenu from '../../globalMenu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import dashboardStyles from './../../../assets/styles/componentStyles/callAnalysisStyles/callAnalysisDashboard.module.scss';
import style from '../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss'
import { formatDateAccordingToTimezoneUsingMoment, hasContactManageAccess } from '../../../utils/constants';
import globalStyles from "../../../assets/styles/global.module.scss";
import { UserContext, UserContextPayload } from '../../../context/userContext';
import GlobalTooltip from '../../utility/tooltip/globalTooltip';

interface MemoizedContactTableRowProps {
    data: campaignContact,
    index: number, 
    userTimezone: string,
    campaignContactFieldData: getListOfCampaignContactFieldResponse | null,
    anchorEl: null | SVGSVGElement, 
    currentRowIndex: number | null,
    menuItem: globalMenuItemData[];
    isContactListArchived: boolean,
    handleSelectContact: (data: campaignContact) => void;
    handleMenuOpen: (event: React.MouseEvent<SVGSVGElement, MouseEvent>, rowIndex: number) => void;
    handleMenuClose: () => void;
    sortField: (field: campaignContactFieldData[]) => campaignContactFieldData[],
    isMenuOpen: boolean,
    isCheckBoxChecked: boolean,
    shouldShowCheckbox: () => boolean,
} 

const MemoizedContactTableRow: React.FC<MemoizedContactTableRowProps> = React.memo((props) => {

    const { data, index, userTimezone, campaignContactFieldData, anchorEl, currentRowIndex, menuItem, isContactListArchived, handleSelectContact, handleMenuOpen, handleMenuClose, sortField, isCheckBoxChecked, shouldShowCheckbox } = props;

    const { userDetail, apiRoutesPermissionAssignment } = useContext(UserContext) as UserContextPayload;

    const ContactFieldCellContent = ({ value }: { value: string }) => {
        const contentRef = useRef<HTMLDivElement | null>(null);
        const [isTruncated, setIsTruncated] = useState(false);

        useEffect(() => {
            const element = contentRef.current;
            if (!element) return;

            setIsTruncated(element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth);
        }, [value]);

        const content = (
            <div ref={contentRef} className={style.contactTableCell}>
                {value}
            </div>
        );

        return isTruncated ? (
            <GlobalTooltip title={value} placement="top" arrow>
                {content}
            </GlobalTooltip>
        ) : (
            content
        );
    };

    return (
        <>
            <Tooltip title={data.flag_deleted ? "This Contact has been deleted." : ""} placement='top-start'> 
                <>
                    {shouldShowCheckbox() && 
                        <TableCell
                            sx={{
                                borderLeft: data.flag_deleted ? '4px solid #CF2C2C' : '4px solid transparent',
                                borderTopLeftRadius: data.flag_deleted ? "10%" : "0",
                                borderBottomLeftRadius: data.flag_deleted ? "10%" : "0", 
                            }}
                            className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.checkBoxCellWidth} ${globalStyles.displayFlexStyle} ${globalStyles.alignItemCenter}`}>
                            <Checkbox
                                color='secondary'
                                inputProps={{ 'aria-label': 'controlled' }}
                                checked={isCheckBoxChecked}
                                onChange={(e) => handleSelectContact(data)}
                                disabled={data.flag_deleted}
                                className={style.checkBoxContact}
                            />
                        </TableCell>
                    }
                    {/* {sortFields(data.contact_data).map((field, ind) => (
                        <TableCell key={ind} className={dashboardStyles.tableBodySubHeadingFontStyle}>{field.contact_data_value.length > 0 ? field.contact_data_value : "-"}</TableCell>
                    ))} */}
                    {campaignContactFieldData && sortField(campaignContactFieldData?.contact_field_data).map((field, ind) => (
                        <TableCell key={ind} className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.fieldCellWidth} ${globalStyles.displayFlexStyle} ${globalStyles.alignItemCenter}`}>
                            <ContactFieldCellContent value={data.contact_data?.find((contact: campaignContactData) => contact.contact_field_id == field.contact_field_id)?.contact_data_value || "-"} />
                        </TableCell>
                    ))}
                    <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.tagCellWidth} ${globalStyles.displayFlexStyle} ${globalStyles.alignItemCenter}`}>
                        <Grid item>
                            <Grid container gap={1} flexWrap={"wrap"}>
                                {data.contact_tag_assignment.length > 0 ? data.contact_tag_assignment.map((tag, ind) => {
                                    return (
                                        <Grid item key={ind} className={style.tagContainerGrid}>
                                            <Typography className={style.tagNameTypography}>{tag.contact_tag_name}</Typography>
                                        </Grid>
                                    );
                                })
                                    :
                                    "-"
                                }
                            </Grid>
                        </Grid>
                    </TableCell>
                    <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.fieldCellWidth} ${globalStyles.displayFlexStyle} ${globalStyles.alignItemCenter}`}>{formatDateAccordingToTimezoneUsingMoment(data.created_at_timestamp, userTimezone)}</TableCell>
                   {hasContactManageAccess(apiRoutesPermissionAssignment, userDetail) &&
                        <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.lastColumnSticky} ${style.moreVertIcon} ${style.checkBoxCellWidth} ${globalStyles.displayFlexStyle} ${globalStyles.alignItemCenter} ${globalStyles.contentEnd}`}>
                            {!isContactListArchived &&
                                <>
                                    <MoreVertIcon
                                        sx={{ cursor: data.flag_deleted ? "not-allowed" : "pointer", opacity: data.flag_deleted ? 0.5 : 1 }}
                                        onClick={(e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
                                            if (!data.flag_deleted) handleMenuOpen(e, data.contact_id);
                                        }}
                                    />
                                    <GlobalMenu
                                        anchorEl={anchorEl}
                                        handleMenuClose={handleMenuClose}
                                        menuItemData={menuItem.map((item) => ({ ...item, isDisable: Boolean(item.isDisable) || data.flag_deleted }))}
                                        open={data.contact_id === currentRowIndex}
                                        additionalField={data}
                                    />
                                </>
                            }
                        </TableCell>
                   }
                </>
            </Tooltip>
        </>
    )
}, (prev, next) => {
    return (
        prev.data == next.data &&
        prev.isMenuOpen == next.isMenuOpen  &&
        prev.isContactListArchived == next.isContactListArchived &&
        prev.isCheckBoxChecked == next.isCheckBoxChecked
    );
})

export default MemoizedContactTableRow
